import axios from 'axios';

// Amazon FBA Removal Fee Rate Card (as of 2023)
// Source: https://sellercentral.amazon.com/gp/help/external/G201074400
const removalFeeRates = {
  // Standard size items
  standard: [
    { maxWeight: 0.5, fee: 1.04 },
    { maxWeight: 1.0, fee: 1.53 },
    { maxWeight: 2.0, fee: 2.27 },
    { maxWeight: Infinity, feeBase: 2.89, feePerPound: 1.06, threshold: 2.0 } // More than 2 lb: $2.89 + $1.06/lb above 2 lb
  ],
  // Large bulky, extra-large and special handling items
  oversized: [
    { maxWeight: 1.0, fee: 3.12 },
    { maxWeight: 2.0, fee: 4.30 },
    { maxWeight: 4.0, fee: 6.36 },
    { maxWeight: 10.0, fee: 10.04 },
    { maxWeight: Infinity, feeBase: 14.32, feePerPound: 1.06, threshold: 10.0 } // More than 10 lb: $14.32 + $1.06/lb above 10 lb
  ]
};

// ASIN Data API configuration (commented out but kept for reference)
// 
// const ASIN_API_URL = 'https://api.asindataapi.com/request';

// Keepa API configuration
const KEEPA_API_URL = 'https://api.keepa.com/product';

// Use the environment variable for the API key
const KEEPA_API_KEY = process.env.REACT_APP_KEEPA_API_KEY;

// Lookup ASIN details using Keepa API
export const lookupAsinDetails = async (asin) => {
  try {
    console.log(`🔍 Looking up ASIN: ${asin} via Keepa API`);
    
    // First, try to get data from the Keepa API
    console.time('API Request Duration');
    
    // According to Keepa docs, we need to format the request properly
    const response = await axios.get(KEEPA_API_URL, { 
      params: {
        key: KEEPA_API_KEY,
        domain: 1,
        asin: asin
      },
      timeout: 15000 // 15 second timeout
    });
    
    console.timeEnd('API Request Duration');
    console.log('📥 Keepa API response status:', response.status);
    
    // Log the full response for debugging
    console.log('📦 Full Keepa API response:', response.data);
    
    // Check if the API returned valid data
    if (response.data && response.data.products && response.data.products.length > 0) {
      const product = response.data.products[0];
      
      // Log all available product fields
      console.log('📦 Available product fields:', Object.keys(product));
      console.log('📦 Product title:', product.title);
      console.log('📦 Product ASIN:', product.asin);
      
      // Extract product details
      let dimensions = 'Unknown';
      let weight = 1.0; // Default weight in pounds
      let isStandardSize = true;
      let category = 'Unknown';
      let title = product.title || `Product (${asin})`;
      
      // Try to find dimensions - prefer package dimensions over product dimensions
      if (product.packageLength && product.packageWidth && product.packageHeight) {
        // Convert mm to inches (Keepa uses mm)
        const lengthInches = (product.packageLength / 25.4).toFixed(2);
        const widthInches = (product.packageWidth / 25.4).toFixed(2);
        const heightInches = (product.packageHeight / 25.4).toFixed(2);
        
        dimensions = `${lengthInches} x ${widthInches} x ${heightInches} inches`;
        console.log(`📏 Using package dimensions: ${dimensions}`);
      } else if (product.itemLength && product.itemWidth && product.itemHeight) {
        // Fall back to item dimensions if package dimensions aren't available
        const lengthInches = (product.itemLength / 25.4).toFixed(2);
        const widthInches = (product.itemWidth / 25.4).toFixed(2);
        const heightInches = (product.itemHeight / 25.4).toFixed(2);
        
        dimensions = `${lengthInches} x ${widthInches} x ${heightInches} inches`;
        console.log(`📏 Using item dimensions: ${dimensions}`);
      } else {
        // If no dimensions found, check if there's a feature that contains dimensions
        if (product.features) {
          for (const feature of product.features) {
            if (feature.toLowerCase().includes('dimension') || 
                feature.toLowerCase().includes(' x ') || 
                feature.toLowerCase().includes('inches')) {
              
              // Try to extract dimensions from the feature text
              const dimMatch = feature.match(/(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)/);
              if (dimMatch) {
                dimensions = `${dimMatch[1]} x ${dimMatch[2]} x ${dimMatch[3]} inches`;
                console.log(`📏 Extracted dimensions from features: ${dimensions}`);
                break;
              }
            }
          }
        }
      }
      
      // Try to find weight - prefer package weight over item weight
      if (product.packageWeight) {
        // Convert grams to pounds (Keepa appears to use grams, not kg)
        weight = parseFloat((product.packageWeight / 453.592).toFixed(2));
        console.log(`⚖️ Raw package weight from API: ${product.packageWeight}`);
        console.log(`⚖️ Using package weight: ${weight} lbs (converted from grams)`);
      } else if (product.itemWeight) {
        // Fall back to item weight if package weight isn't available
        weight = parseFloat((product.itemWeight / 453.592).toFixed(2));
        console.log(`⚖️ Raw item weight from API: ${product.itemWeight}`);
        console.log(`⚖️ Using item weight: ${weight} lbs (converted from grams)`);
      } else {
        // If no weight found, check if there's a feature that contains weight
        if (product.features) {
          for (const feature of product.features) {
            if (feature.toLowerCase().includes('weight') || 
                feature.toLowerCase().includes('pounds') || 
                feature.toLowerCase().includes('lbs')) {
              
              // Try to extract weight from the feature text
              const weightMatch = feature.match(/(\d+\.?\d*)\s*(pounds|pound|lbs|lb|ounces|ounce|oz)/i);
              if (weightMatch) {
                let extractedWeight = parseFloat(weightMatch[1]);
                
                // Convert to pounds if needed
                if (weightMatch[2].toLowerCase().includes('oz') || 
                    weightMatch[2].toLowerCase().includes('ounce')) {
                  extractedWeight /= 16; // Convert ounces to pounds
                }
                
                weight = parseFloat(extractedWeight.toFixed(2));
                console.log(`⚖️ Extracted weight from features: ${weight} lbs`);
                break;
              }
            }
          }
        }
      }
      
      // Sanity check for weight - if it's still unreasonably high, it might be in a different unit
      if (weight > 100) {
        console.log(`⚠️ Weight still seems unreasonably high (${weight} lbs), trying alternative conversion`);
        
        // Try dividing by 1000 (if it was actually kg instead of g)
        if (product.packageWeight) {
          weight = parseFloat((product.packageWeight / 453592).toFixed(2)); // Convert kg to lbs (1kg = 2.20462 lbs)
          console.log(`⚖️ Alternative conversion: ${weight} lbs (assuming original was in kg)`);
        } else if (product.itemWeight) {
          weight = parseFloat((product.itemWeight / 453592).toFixed(2));
          console.log(`⚖️ Alternative conversion: ${weight} lbs (assuming original was in kg)`);
        }
        
        // If still too high, cap it
        if (weight > 100) {
          console.log(`⚠️ Weight is still unreasonable, capping at 10 lbs`);
          weight = 10.00;
        }
      }
      
      // Extract product category
      if (product.categoryTree && product.categoryTree.length > 0) {
        // Get the most specific category (last one in the tree)
        category = product.categoryTree[product.categoryTree.length - 1];
        console.log(`🏷️ Using category: ${category}`);
      }
      
      // Determine if the item is standard size or oversized
      isStandardSize = determineIfStandardSize(dimensions, weight, category);
      
      return {
        title,
        dimensions,
        weight,
        isStandardSize,
        category
      };
    } else {
      console.log('❌ No product data found in Keepa API response');
      if (response.data && response.data.error) {
        console.log('Error from Keepa API:', response.data.error);
        throw new Error(`Keepa API error: ${response.data.error}`);
      }
      throw new Error('Product information not found. Please verify the ASIN is correct and active on Amazon.');
    }
  } catch (error) {
    console.error('❌ Error looking up ASIN details:', error);
    
    // Provide a user-friendly error message
    if (error.response) {
      console.log('Error response status:', error.response.status);
      console.log('Error response data:', error.response.data);
      
      if (error.response.status === 429) {
        throw new Error('API rate limit exceeded. Please try again in a few minutes.');
      } else if (error.response.status === 404) {
        throw new Error('Product not found. Please verify the ASIN is correct and active on Amazon.');
      } else if (error.response.status === 400) {
        // For 400 errors, let's provide more specific information
        if (error.response.data && error.response.data.error) {
          throw new Error(`API error: ${error.response.data.error}`);
        } else {
          throw new Error('Invalid request to API. Please check the ASIN format.');
        }
      } else {
        throw new Error(`API error (${error.response.status}): ${error.response.data?.message || 'Unknown error'}`);
      }
    } else if (error.request) {
      console.log('No response received:', error.request);
      throw new Error('No response from API. Please check your internet connection and try again.');
    } else {
      console.log('Error message:', error.message);
      throw new Error(`Error: ${error.message}`);
    }
  }
};

// Keep the original ASIN Data API implementation as a comment for reference
/*
export const lookupAsinDetails = async (asin) => {
  try {
    console.log(`🔍 Looking up ASIN: ${asin} via ASIN Data API`);
    
    // First, try to get data from the API
    console.time('API Request Duration');
    const response = await axios.get(ASIN_API_URL, { 
      params: {
        api_key: ASIN_API_KEY,
        amazon_domain: 'amazon.com',
        asin: asin,
        type: 'product'  // This is the correct parameter for product data
      },
      timeout: 15000 // 15 second timeout
    });
    console.timeEnd('API Request Duration');
    
    console.log('📥 API response status:', response.status);
    
    // Log the full response data structure for debugging
    console.log('📦 API response data structure:', Object.keys(response.data));
    
    // Check if the API returned valid data
    if (response.data && response.data.product) {
      const product = response.data.product;
      
      // Log all available product fields
      console.log('📦 Available product fields:', Object.keys(product));
      console.log('📦 Product title:', product.title);
      console.log('📦 Product ASIN:', product.asin);
      
      // Log specific fields we're interested in
      console.log('📏 Dimensions data:', product.dimensions);
      console.log('⚖️ Weight data:', product.weight);
      console.log('🏷️ Categories data:', product.categories);
      
      // Extract product details
      let dimensions = 'Unknown';
      let weight = 1.0; // Default weight in pounds
      let isStandardSize = true;
      let category = 'Unknown';
      let title = product.title || `Product (${asin})`;
      
      // Try to find dimensions in various possible fields
      if (product.dimensions) {
        dimensions = product.dimensions;
        console.log(`📏 Using dimensions from dimensions field: ${dimensions}`);
      } else if (product.product_information && product.product_information.dimensions) {
        dimensions = product.product_information.dimensions;
        console.log(`📏 Using dimensions from product_information: ${dimensions}`);
      } else if (product.specifications) {
        // Look for dimensions in specifications
        const dimSpec = product.specifications.find(spec => 
          spec.name && (spec.name.toLowerCase().includes('dimension') || 
                        spec.name.toLowerCase().includes('size')));
        if (dimSpec) {
          dimensions = dimSpec.value;
          console.log(`📏 Using dimensions from specifications: ${dimensions}`);
        }
      }
      
      // Try to find weight in various possible fields
      if (product.weight) {
        console.log(`⚖️ Using weight from weight field: ${product.weight}`);
        // Try to parse the weight from the string (e.g., "1.5 pounds" or "5.5 ounces")
        const weightMatch = product.weight.match(/(\d+\.?\d*)/);
        if (weightMatch) {
          weight = parseFloat(weightMatch[0]);
          
          // Convert to pounds based on the unit
          if (product.weight.toLowerCase().includes('ounce') || 
              product.weight.toLowerCase().includes('oz')) {
            console.log(`🔄 Converting ${weight} ounces to pounds`);
            weight = weight / 16; // Convert ounces to pounds
          } else if (product.weight.toLowerCase().includes('kg') || 
              product.weight.toLowerCase().includes('kilogram')) {
            console.log(`🔄 Converting ${weight} kg to pounds`);
            weight = weight * 2.20462; // Convert kg to pounds
          } else if (product.weight.toLowerCase().includes('gram')) {
            console.log(`🔄 Converting ${weight} grams to pounds`);
            weight = weight / 453.592; // Convert grams to pounds
          }
          
          console.log(`⚖️ Normalized weight in pounds: ${weight.toFixed(2)} lbs`);
        } else {
          console.log('⚠️ Could not parse weight from string:', product.weight);
        }
      } else if (product.product_information && product.product_information.weight) {
        console.log(`⚖️ Using weight from product_information: ${product.product_information.weight}`);
        // Parse weight from product_information
        const weightMatch = product.product_information.weight.match(/(\d+\.?\d*)/);
        if (weightMatch) {
          weight = parseFloat(weightMatch[0]);
          
          // Convert to pounds based on the unit
          if (product.product_information.weight.toLowerCase().includes('ounce') || 
              product.product_information.weight.toLowerCase().includes('oz')) {
            console.log(`🔄 Converting ${weight} ounces to pounds`);
            weight = weight / 16; // Convert ounces to pounds
          } else if (product.product_information.weight.toLowerCase().includes('kg') || 
              product.product_information.weight.toLowerCase().includes('kilogram')) {
            console.log(`🔄 Converting ${weight} kg to pounds`);
            weight = weight * 2.20462; // Convert kg to pounds
          } else if (product.product_information.weight.toLowerCase().includes('gram')) {
            console.log(`🔄 Converting ${weight} grams to pounds`);
            weight = weight / 453.592; // Convert grams to pounds
          }
          
          console.log(`⚖️ Normalized weight in pounds: ${weight.toFixed(2)} lbs`);
        }
      } else if (product.specifications) {
        // Look for weight in specifications
        const weightSpec = product.specifications.find(spec => 
          spec.name && spec.name.toLowerCase().includes('weight'));
        if (weightSpec) {
          console.log(`⚖️ Using weight from specifications: ${weightSpec.value}`);
          const weightMatch = weightSpec.value.match(/(\d+\.?\d*)/);
          if (weightMatch) {
            weight = parseFloat(weightMatch[0]);
            
            // Convert to pounds based on the unit
            if (weightSpec.value.toLowerCase().includes('ounce') || 
                weightSpec.value.toLowerCase().includes('oz')) {
              console.log(`🔄 Converting ${weight} ounces to pounds`);
              weight = weight / 16; // Convert ounces to pounds
            } else if (weightSpec.value.toLowerCase().includes('kg') || 
                weightSpec.value.toLowerCase().includes('kilogram')) {
              console.log(`🔄 Converting ${weight} kg to pounds`);
              weight = weight * 2.20462; // Convert kg to pounds
            } else if (weightSpec.value.toLowerCase().includes('gram')) {
              console.log(`🔄 Converting ${weight} grams to pounds`);
              weight = weight / 453.592; // Convert grams to pounds
            }
            
            console.log(`⚖️ Normalized weight in pounds: ${weight.toFixed(2)} lbs`);
          }
        }
      }
      
      // Extract product category
      if (product.categories && product.categories.length > 0) {
        // Ensure category is a string
        const rawCategory = product.categories[0];
        category = typeof rawCategory === 'string' ? rawCategory : 
                  (rawCategory && typeof rawCategory.toString === 'function' ? 
                   rawCategory.toString() : 'Unknown');
        console.log(`🏷️ Using category: ${category}`);
      } else {
        console.log('⚠️ No categories found, using default');
      }
      
      // Determine if the item is standard size or oversized
      isStandardSize = determineIfStandardSize(dimensions, weight, category);
      
      return {
        title,
        dimensions,
        weight,
        isStandardSize,
        category
      };
    } else {
      console.log('❌ No product data found in API response');
      console.log('Full response:', response.data);
      throw new Error('Product information not found. Please verify the ASIN is correct and active on Amazon.');
    }
  } catch (error) {
    console.error('❌ Error looking up ASIN details:', error);
    
    // Provide a user-friendly error message
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.log('Error response status:', error.response.status);
      console.log('Error response data:', error.response.data);
      
      if (error.response.status === 429) {
        throw new Error('API rate limit exceeded. Please try again in a few minutes.');
      } else if (error.response.status === 404) {
        throw new Error('Product not found. Please verify the ASIN is correct and active on Amazon.');
      } else {
        throw new Error(`API error (${error.response.status}): ${error.response.data.message || 'Unknown error'}`);
      }
    } else if (error.request) {
      // The request was made but no response was received
      console.log('No response received:', error.request);
      throw new Error('No response from API. Please check your internet connection and try again.');
    } else {
      // Something happened in setting up the request that triggered an Error
      console.log('Error message:', error.message);
      throw new Error(`Error: ${error.message}`);
    }
  }
};
*/

// Determine if an item is standard size based on dimensions and weight
function determineIfStandardSize(dimensions, weight, category) {
  console.log(`🔍 Determining size tier for: dimensions=${dimensions}, weight=${weight}lbs, category=${category}`);
  
  // Ensure category is a string before using toLowerCase
  const categoryStr = typeof category === 'string' ? category : 
                     (category && typeof category.toString === 'function' ? category.toString() : 'Unknown');
  
  // Special handling items are always considered oversized for removal fees
  if (['Apparel', 'Clothing', 'Shoes', 'Watches', 'Jewelry', 'Dangerous Goods'].some(cat => 
      categoryStr.toLowerCase().includes(cat.toLowerCase()))) {
    console.log(`👕 Product is in special handling category: ${categoryStr}`);
    return false;
  }
  
  // If weight is over 20 lbs, it's definitely not standard size
  if (weight > 20) {
    console.log(`⚖️ Weight (${weight}lbs) exceeds 20lbs maximum for standard size`);
    return false;
  }
  
  // Try to parse dimensions
  try {
    // Handle different dimension formats
    // Format could be "5.1 x 3.2 x 0.8 inches" or "5.1 x 3.2 x 0.8" or other variations
    const dimRegex = /(\d+\.?\d*)\s*[xX]\s*(\d+\.?\d*)\s*[xX]\s*(\d+\.?\d*)/;
    const match = dimensions.match(dimRegex);
    
    if (match) {
      // Extract the three dimensions
      const dims = [
        parseFloat(match[1]),
        parseFloat(match[2]),
        parseFloat(match[3])
      ];
      
      console.log(`📏 Parsed dimensions: ${dims.join(' x ')}`);
      
      // Sort dimensions from largest to smallest
      dims.sort((a, b) => b - a);
      
      const [longest, median, shortest] = dims;
      console.log(`📏 Sorted dimensions (longest, median, shortest): ${longest} x ${median} x ${shortest}`);
      
      // Standard size criteria
      // Max weight: 20 lbs
      // Max dimensions: 18" x 14" x 8"
      const isStandard = weight <= 20 && longest <= 18 && median <= 14 && shortest <= 8;
      
      if (!isStandard) {
        if (longest > 18) {
          console.log(`📏 Longest dimension (${longest}") exceeds 18" maximum for standard size`);
        }
        if (median > 14) {
          console.log(`📏 Median dimension (${median}") exceeds 14" maximum for standard size`);
        }
        if (shortest > 8) {
          console.log(`📏 Shortest dimension (${shortest}") exceeds 8" maximum for standard size`);
        }
      }
      
      console.log(`📦 Size classification result: ${isStandard ? 'Standard size' : 'Large bulky/special handling'}`);
      return isStandard;
    } else {
      console.log(`⚠️ Could not parse dimensions from string: ${dimensions}`);
    }
  } catch (e) {
    console.error('❌ Error parsing dimensions:', e);
  }
  
  // If we can't determine for sure, default to standard size
  console.log('⚠️ Could not determine size tier with certainty, defaulting to standard size');
  return true;
}

// Generate mock data for an ASIN (fallback if API fails)
function generateMockData(asin) {
  // Random product categories
  const categories = ['Books', 'Electronics', 'Home', 'Kitchen', 'Toys', 'Apparel', 'Sports Equipment'];
  const category = categories[Math.floor(Math.random() * categories.length)];
  
  // Generate random dimensions and weight
  let dimensions, weight;
  
  // 50% chance of standard size, 50% chance of oversize
  if (Math.random() < 0.5) {
    // Standard size
    const length = 5 + Math.random() * 13; // 5-18 inches
    const width = 4 + Math.random() * 10; // 4-14 inches
    const height = 0.5 + Math.random() * 7.5; // 0.5-8 inches
    dimensions = `${length.toFixed(1)} x ${width.toFixed(1)} x ${height.toFixed(1)} inches`;
    weight = 0.1 + Math.random() * 19.9; // 0.1-20 lbs
  } else {
    // Oversize
    const length = 19 + Math.random() * 30; // 19-49 inches
    const width = 15 + Math.random() * 15; // 15-30 inches
    const height = 9 + Math.random() * 11; // 9-20 inches
    dimensions = `${length.toFixed(1)} x ${width.toFixed(1)} x ${height.toFixed(1)} inches`;
    weight = 20.1 + Math.random() * 29.9; // 20.1-50 lbs
  }
  
  // Determine if the item is standard size
  const standardSize = determineIfStandardSize(dimensions, weight, category);
  
  return {
    title: `${category} Item (${asin})`,
    isStandardSize: standardSize,
    dimensions,
    weight: parseFloat(weight.toFixed(1)),
    category
  };
}

// Calculate the removal fee based on weight and size category
const calculateRemovalFee = (weight, isStandardSize) => {
  console.log(`📊 Calculating fee for ${isStandardSize ? 'standard' : 'oversized'} item weighing ${weight} lbs`);
  
  if (isStandardSize) {
    // Standard-size fee structure
    if (weight <= 0.5) {
      return 1.04;
    } else if (weight <= 1.0) {
      return 1.53;
    } else if (weight <= 2.0) {
      return 2.27;
    } else {
      // More than 2 lbs: $2.89 + $1.06/lb above 2 lb
      return 2.89 + (Math.max(0, weight - 2) * 1.06);
    }
  } else {
    // Oversized fee structure
    if (weight <= 1.0) {
      return 3.12;
    } else if (weight <= 2.0) {
      return 4.30;
    } else if (weight <= 4.0) {
      return 6.36;
    } else if (weight <= 10.0) {
      return 10.04;
    } else {
      // More than 10 lbs: $14.32 + $1.06/lb above 10 lb
      return 14.32 + (Math.max(0, weight - 10) * 1.06);
    }
  }
};

// Calculate dimensional weight based on dimensions and size category
function calculateDimensionalWeight(dimensions, isStandardSize) {
  try {
    console.log(`🔍 Parsing dimensions string: "${dimensions}"`);
    
    // Try multiple regex patterns to handle different dimension formats
    let match = null;
    
    // Pattern 1: Standard "L x W x H" format
    const standardRegex = /(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)/;
    match = dimensions.match(standardRegex);
    
    // Pattern 2: Dimensions with symbols like ‎7"L x 17.02"W x 13"H
    if (!match) {
      const symbolRegex = /(\d+\.?\d*)[\""]?[LWHDlwhd]?\s*[xX×]\s*(\d+\.?\d*)[\""]?[LWHDlwhd]?\s*[xX×]\s*(\d+\.?\d*)[\""]?[LWHDlwhd]?/;
      match = dimensions.match(symbolRegex);
    }
    
    // Pattern 3: Dimensions with units at the end like "7 x 17.02 x 13 inches"
    if (!match) {
      const unitsRegex = /(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)\s*[xX×]\s*(\d+\.?\d*)\s*(?:inches|inch|in|cm|mm)/i;
      match = dimensions.match(unitsRegex);
    }
    
    if (match) {
      // Extract the three dimensions
      let length = parseFloat(match[1]);
      let width = parseFloat(match[2]);
      let height = parseFloat(match[3]);
      
      console.log(`📏 Successfully parsed dimensions: ${length} × ${width} × ${height}`);
      
      // Sort dimensions from largest to smallest for consistency
      [length, width, height] = [length, width, height].sort((a, b) => b - a);
      console.log(`📏 Sorted dimensions (L×W×H): ${length} × ${width} × ${height}`);
      
      // For large bulky items, apply minimum width and height of 2 inches
      if (!isStandardSize) {
        width = Math.max(width, 2);
        height = Math.max(height, 2);
        console.log(`📏 Applying minimum width/height of 2" for large bulky item: ${length} × ${width} × ${height}`);
      }
      
      // Calculate cubic inches
      const cubicInches = length * width * height;
      
      // Calculate cubic feet (1728 cubic inches = 1 cubic foot)
      const cubicFeet = cubicInches / 1728;
      console.log(`📦 Volume: ${cubicInches.toFixed(2)} cubic inches = ${cubicFeet.toFixed(3)} cubic feet`);
      
      // Amazon's dimensional weight formula: cubic inches / 139
      const dimWeight = cubicInches / 139;
      
      console.log(`📐 Dimensional weight calculation: ${length} × ${width} × ${height} = ${cubicInches.toFixed(2)} cubic inches`);
      console.log(`📐 Dimensional weight: ${cubicInches.toFixed(2)} / 139 = ${dimWeight.toFixed(2)} lbs`);
      
      return {
        dimWeight: dimWeight,
        cubicFeet: cubicFeet,
        dimensions: {
          length: length,
          width: width,
          height: height
        }
      };
    } else {
      console.log(`❌ Failed to parse dimensions from string: "${dimensions}"`);
      
      // Try to extract any numbers from the string as a last resort
      const numbers = dimensions.match(/\d+\.?\d*/g);
      if (numbers && numbers.length >= 3) {
        console.log(`⚠️ Attempting to use raw numbers from string: ${numbers.slice(0, 3).join(', ')}`);
        let [length, width, height] = numbers.slice(0, 3).map(n => parseFloat(n));
        
        // Sort dimensions
        [length, width, height] = [length, width, height].sort((a, b) => b - a);
        
        // Calculate with these dimensions
        const cubicInches = length * width * height;
        const cubicFeet = cubicInches / 1728;
        const dimWeight = cubicInches / 139;
        
        console.log(`📦 Volume (best guess): ${cubicInches.toFixed(2)} cubic inches = ${cubicFeet.toFixed(3)} cubic feet`);
        console.log(`📐 Dimensional weight (best guess): ${dimWeight.toFixed(2)} lbs`);
        
        return {
          dimWeight: dimWeight,
          cubicFeet: cubicFeet,
          dimensions: {
            length: length,
            width: width,
            height: height
          }
        };
      }
    }
  } catch (e) {
    console.error('❌ Error calculating dimensional weight:', e);
  }
  
  // Return default values if calculation fails
  console.log('⚠️ Using default values for dimensional weight calculation');
  return {
    dimWeight: 0,
    cubicFeet: 0,
    dimensions: {
      length: 0,
      width: 0,
      height: 0
    }
  };
}

// Calculate removal cost
export const calculateRemovalCost = (asinDetails, quantity) => {
  console.log('💰 Calculating removal cost for:', asinDetails);
  console.log(`🔢 Quantity: ${quantity}`);
  
  // Ensure we have valid input data
  const isStandardSize = asinDetails.isStandardSize !== undefined ? asinDetails.isStandardSize : true;
  const dimensions = asinDetails.dimensions || '10 x 8 x 2 inches';
  const weight = asinDetails.weight || 1.0; // Default to 1 lb if weight is missing
  const category = asinDetails.category || 'Other';
  const title = asinDetails.title || 'Unknown Product';
  
  console.log(`📦 Product: ${title}`);
  console.log(`📏 Dimensions: ${dimensions}`);
  console.log(`⚖️ Actual weight: ${weight}lbs`);
  console.log(`🏷️ Category: ${category}`);
  console.log(`📦 Size tier: ${isStandardSize ? 'Standard size' : 'Large bulky/special handling'}`);
  
  // Calculate dimensional weight using the correct formula
  const dimensionalInfo = calculateDimensionalWeight(dimensions, isStandardSize);
  const dimensionalWeight = dimensionalInfo.dimWeight;
  const cubicFeet = dimensionalInfo.cubicFeet;
  const sortedDimensions = dimensionalInfo.dimensions;
  
  // Use the greater of actual weight or dimensional weight
  let shippingWeight = Math.max(weight, dimensionalWeight);
  console.log(`⚖️ Using shipping weight: ${shippingWeight.toFixed(2)}lbs (greater of actual weight ${weight.toFixed(2)}lbs or dimensional weight ${dimensionalWeight.toFixed(2)}lbs)`);
  
  // Round weight according to Amazon's rules
  let roundedWeight;
  
  if (isStandardSize && shippingWeight < 1) {
    // For standard-size items under 1 lb, round up to nearest 0.1 lb
    roundedWeight = Math.ceil(shippingWeight * 10) / 10;
    console.log(`⚖️ Rounding standard-size item weight to nearest 0.1 lb: ${shippingWeight.toFixed(2)} → ${roundedWeight.toFixed(1)}`);
  } else {
    // For all other items, round up to nearest whole pound
    roundedWeight = Math.ceil(shippingWeight);
    console.log(`⚖️ Rounding weight to nearest whole pound: ${shippingWeight.toFixed(2)} → ${roundedWeight}`);
  }
  
  // Calculate the cost per unit using the exact rate card values
  const costPerUnit = calculateRemovalFee(roundedWeight, isStandardSize);
  console.log(`💲 Cost per unit: $${costPerUnit.toFixed(2)}`);
  
  const totalCost = costPerUnit * quantity;
  console.log(`💲 Total cost for ${quantity} units: $${totalCost.toFixed(2)}`);
  
  return {
    title,
    sizeTier: isStandardSize ? 'Standard size' : 'Large bulky/special handling',
    dimensions: dimensions,
    sortedDimensions: `${sortedDimensions.length.toFixed(2)} × ${sortedDimensions.width.toFixed(2)} × ${sortedDimensions.height.toFixed(2)} inches`,
    weight: weight,
    dimensionalWeight: parseFloat(dimensionalWeight.toFixed(2)),
    cubicFeet: parseFloat(cubicFeet.toFixed(3)),
    shippingWeight: parseFloat(shippingWeight.toFixed(2)),
    roundedWeight: roundedWeight,
    sizeCategory: isStandardSize ? 'Standard size' : 'Large bulky/special handling',
    costPerUnit: costPerUnit,
    totalCost: totalCost,
    policySource: 'Amazon FBA Removal Fee Rate Card'
  };
};